import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSafeguardingAlerts, reviewSafeguardingFlag } from '../../services/api';
import SignOutButton from '../../components/SignOutButton';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  surfaceSubtle: '#F1F5F9',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  green: '#10B981',
  greenDark: '#059669',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
  greenText: '#065F46',
  amber: '#F59E0B',
  amberSoft: '#FEF3C7',
  amberBorder: '#FDE68A',
  amberText: '#92400E',
  red: '#EF4444',
  redDark: '#B91C1C',
  redSoft: '#FEF2F2',
  // Deeper red used for "critical" severity — keeps the same red family as
  // "high" but with a darker bg + saturated border so the escalation reads.
  redDeeper: '#FEE2E2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
  skeletonBase: '#F1F5F9',
  skeletonHighlight: '#E2E8F0',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// ─── Severity & flag-type maps ────────────────────────────────────────────────

const SEVERITY_COLORS = {
  low:      { accent: C.borderStrong, badgeBg: C.surfaceMuted, badgeText: C.textMuted, badgeBorder: C.border,        label: 'Low' },
  medium:   { accent: C.amber,        badgeBg: C.amberSoft,    badgeText: C.amberText, badgeBorder: C.amberBorder,   label: 'Medium' },
  high:     { accent: C.red,          badgeBg: C.redSoft,      badgeText: C.redText,   badgeBorder: C.redBorder,     label: 'High' },
  critical: { accent: C.redDark,      badgeBg: C.redDeeper,    badgeText: C.redDark,   badgeBorder: C.red,           label: 'Critical' },
};

const FLAG_TYPE_LABEL = {
  jailbreak:    'Inappropriate request',
  safeguarding: 'Safeguarding concern',
  child_report: 'Reported by child',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

// ─── Brand mark (matches Dashboard / SessionDetail) ───────────────────────────

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0, 102, 255, 0.3)',
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT,
        fontSize: 17,
        fontWeight: 700,
        color: C.text,
        letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// Build the mailto for "Contact support". Encodes the subject and body so
// the formatted-date placeholder survives email clients that don't run
// JavaScript on the link. The fallback flag.created_at handles any tz/locale
// quirks via the existing formatDate helper.
function buildSupportMailto(flag) {
  const dateLabel = formatDate(flag.created_at);
  const subject = `Safeguarding query — ${dateLabel}`;
  const body    = `I have a query about a safeguarding flag in my child session on ${dateLabel}.`;
  return `mailto:contactus@whdventures.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const REVIEW_TYPE_LABEL = {
  no_action:         'No action needed',
  action_taken:      'Action taken',
  contacted_support: 'Contacted support',
};

function FlagCard({ flag, onViewSession, onReviewed }) {
  // The flag is "reviewed" iff the server returned a joined review row.
  // The pre-migration-014 `parent_notified` field is unrelated — it tracks
  // whether a push notification was sent, not whether a parent has acted.
  const reviewed = !!flag.review_id || !!flag.review_type;
  const sev = SEVERITY_COLORS[flag.severity] || SEVERITY_COLORS.low;

  // Local state for the in-card review interaction. `action` is the
  // dialog currently open (null = none, 'note' = action-taken text input).
  // `submitting` blocks repeat clicks while the POST is in flight.
  // `error` surfaces a soft message if the review POST fails.
  const [action, setAction]         = useState(null);
  const [note, setNote]             = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  async function submitReview(reviewType, actionTaken = null) {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await reviewSafeguardingFlag(flag.id, { reviewType, actionTaken });
      onReviewed?.(flag.id, res.data?.review);
    } catch (err) {
      console.error('[Safeguarding] review failed:', err);
      setError("Couldn't record your review. Try again in a moment.");
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderLeft: `4px solid ${sev.accent}`,
      borderRadius: 10,
      padding: '16px 20px',
      fontFamily: BODY_FONT,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 4,
          }}>
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              color: sev.badgeText,
              background: sev.badgeBg,
              border: `1px solid ${sev.badgeBorder}`,
              borderRadius: 999,
              padding: '2px 9px',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>{sev.label}</span>
            {!reviewed && (
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                background: C.red,
                color: '#fff',
                borderRadius: 999,
                padding: '2px 9px',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}>Unreviewed</span>
            )}
            {reviewed && (
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                background: C.greenSoft,
                color: C.greenText,
                border: `1px solid ${C.greenBorder}`,
                borderRadius: 999,
                padding: '2px 9px',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}>Reviewed</span>
            )}
          </div>
          <h3 style={{
            margin: 0,
            fontFamily: HEADING_FONT,
            fontSize: 16,
            fontWeight: 700,
            color: C.text,
            letterSpacing: '-0.01em',
          }}>
            {FLAG_TYPE_LABEL[flag.flag_type] || flag.flag_type}
          </h3>
        </div>
      </div>

      {/* Metadata row */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        fontSize: 12,
        color: C.textMuted,
      }}>
        <span>{formatDate(flag.created_at)}</span>
        {flag.response_withheld === 1 && (
          <>
            <span style={{ color: C.textSubtle, margin: '0 10px' }}>·</span>
            <span style={{
              color: C.redText,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
            }}>
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <rect x="3" y="6" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M5 6V4.5a2 2 0 0 1 4 0V6" stroke="currentColor" strokeWidth="1.4"/>
              </svg>
              Response withheld from child
            </span>
          </>
        )}
      </div>

      {flag.notes && (
        <div style={{
          fontSize: 13,
          color: C.text,
          lineHeight: 1.55,
          background: C.surfaceMuted,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '10px 12px',
          textWrap: 'pretty',
        }}>
          <span style={{ color: C.textSubtle }}>“</span>
          {flag.notes}
          <span style={{ color: C.textSubtle }}>”</span>
        </div>
      )}

      {flag.session_id && (
        <div>
          <button
            className="sa-view-btn"
            onClick={onViewSession}
            style={{
              fontFamily: BODY_FONT,
              fontSize: 13,
              fontWeight: 600,
              padding: '7px 14px',
              background: '#fff',
              color: C.accent,
              border: `1px solid ${C.accentBorder}`,
              borderRadius: 8,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              transition: 'background 160ms ease, border-color 160ms ease',
            }}
          >
            View session
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Reviewed-state summary — replaces the action buttons once a review
          has been recorded. Reviews are append-only (Absolute Rule #10), so
          there is no "edit" path; the audit record stays as written. */}
      {reviewed && (
        <div style={{
          marginTop: 4,
          padding: '10px 12px',
          background: C.greenSoft,
          border: `1px solid ${C.greenBorder}`,
          borderRadius: 8,
          fontSize: 13,
          color: C.greenText,
          lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 700 }}>
            {REVIEW_TYPE_LABEL[flag.review_type] || 'Reviewed'}
            {flag.review_reviewed_at && (
              <span style={{ fontWeight: 500, marginLeft: 8, color: C.greenDark }}>
                · {formatDate(new Date(flag.review_reviewed_at).getTime())}
              </span>
            )}
          </div>
          {flag.review_action_taken && (
            <div style={{ marginTop: 6, color: C.text, fontSize: 13 }}>
              <span style={{ color: C.textSubtle }}>“</span>
              {flag.review_action_taken}
              <span style={{ color: C.textSubtle }}>”</span>
            </div>
          )}
        </div>
      )}

      {/* Review actions — three options per the 16 May 2026 brief. Renders
          only on unreviewed flags. The "action_taken" option opens an inline
          textarea so the parent can record what they did; "contact support"
          opens a pre-filled mailto and records the review in one step. */}
      {!reviewed && action !== 'note' && (
        <div style={{
          marginTop: 4,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <button
            type="button"
            disabled={submitting}
            onClick={() => submitReview('no_action')}
            style={reviewButtonStyle('neutral', submitting)}
          >
            No action needed — mark as reviewed
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => setAction('note')}
            style={reviewButtonStyle('primary', submitting)}
          >
            I have taken action
          </button>
          <a
            href={buildSupportMailto(flag)}
            // Optimistic: open the mail client and record the review so the
            // flag transitions out of unreviewed straight away. If the
            // review POST fails the inline error surfaces, but the mailto
            // still opened — the parent isn't blocked from reaching out.
            onClick={() => submitReview('contacted_support')}
            style={{
              ...reviewButtonStyle('neutral', submitting),
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Contact support
          </a>
        </div>
      )}

      {/* Inline "action taken" textarea — appears when the parent picks
          "I have taken action". Save commits the review; Cancel returns
          to the three buttons. */}
      {!reviewed && action === 'note' && (
        <div style={{
          marginTop: 4,
          padding: '10px 12px',
          background: C.surfaceMuted,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <label style={{
            fontSize: 12,
            fontWeight: 600,
            color: C.text,
          }}>
            What action did you take?
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Spoke with my child, contacted school, booked GP appointment…"
            rows={3}
            maxLength={2000}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontFamily: BODY_FONT,
              fontSize: 13,
              color: C.text,
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              disabled={submitting || note.trim().length === 0}
              onClick={() => submitReview('action_taken', note.trim())}
              style={reviewButtonStyle('primary', submitting || note.trim().length === 0)}
            >
              Save review
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => { setAction(null); setNote(''); }}
              style={reviewButtonStyle('neutral', submitting)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" style={{
          fontSize: 12,
          color: C.redText,
          padding: '6px 8px',
          background: C.redSoft,
          border: `1px solid ${C.redBorder}`,
          borderRadius: 6,
        }}>{error}</div>
      )}
    </div>
  );
}

function reviewButtonStyle(tone, disabled) {
  const palette = tone === 'primary'
    ? { bg: '#0066FF', color: '#fff',     border: '#0066FF' }
    : { bg: '#fff',    color: '#1E293B',  border: '#CBD5E1' };
  return {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12.5,
    fontWeight: 600,
    padding: '7px 12px',
    background: palette.bg,
    color: palette.color,
    border: `1px solid ${palette.border}`,
    borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition: 'background 160ms ease, border-color 160ms ease, opacity 160ms ease',
  };
}

function SkeletonBlock({ width, height, style }) {
  return (
    <div
      aria-hidden="true"
      style={{
        background: C.skeletonBase,
        backgroundImage: `linear-gradient(90deg, ${C.skeletonBase} 0%, ${C.skeletonHighlight} 50%, ${C.skeletonBase} 100%)`,
        backgroundSize: '200% 100%',
        animation: 'sa-shimmer 1.4s ease-in-out infinite',
        borderRadius: 6,
        width,
        height,
        ...style,
      }}
    />
  );
}

function AlertsSkeleton() {
  return (
    <div>
      <SkeletonBlock width={260} height={30} style={{ marginBottom: 12 }} />
      <SkeletonBlock width={440} height={14} style={{ marginBottom: 28 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderLeft: `4px solid ${C.border}`,
            borderRadius: 10,
            padding: '16px 20px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <SkeletonBlock width={80} height={18} style={{ borderRadius: 999 }} />
            </div>
            <SkeletonBlock width={180} height={16} style={{ marginBottom: 8 }} />
            <SkeletonBlock width={140} height={12} style={{ marginBottom: 10 }} />
            <SkeletonBlock width="80%" height={36} style={{ borderRadius: 8 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main alerts page ─────────────────────────────────────────────────────────

export default function SafeguardingAlerts() {
  const [flags, setFlags] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getSafeguardingAlerts()
      .then(r => setFlags(r.data.flags))
      .catch((err) => {
        if (err.response?.status === 401) {
          navigate('/parent/login');
          return;
        }
        setError('Could not load safeguarding alerts.');
      });
  }, [navigate]);

  // "Unreviewed" is now defined by the absence of a review record, not the
  // legacy parent_notified field (which tracks whether a push notification
  // was sent and is unrelated to whether the parent has acted).
  const unreviewed = flags ? flags.filter(f => !f.review_id && !f.review_type) : [];

  function handleReviewed(flagId, review) {
    if (!review) return;
    setFlags(current => current.map(f => (
      f.id === flagId
        ? {
            ...f,
            review_id:           review.id,
            review_type:         review.review_type,
            review_action_taken: review.action_taken,
            review_reviewed_at:  review.reviewed_at,
          }
        : f
    )));
  }

  return (
    <>
      <style>{`
        @keyframes sa-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .sa-back:hover { background: ${C.surfaceMuted}; border-color: ${C.borderStrong}; }
        .sa-view-btn:hover { background: ${C.accentSoft}; border-color: ${C.accent}; }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: BODY_FONT,
        color: C.text,
      }}>
        {/* Top nav */}
        <div style={{
          borderBottom: `1px solid ${C.border}`,
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 5,
        }}>
          <div style={{
            maxWidth: 980,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <LanternBrand />
            <SignOutButton />
          </div>
        </div>

        <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px 56px' }}>

          {/* Back button */}
          {!error && (
            <button
              className="sa-back"
              onClick={() => navigate('/parent/dashboard')}
              style={{
                fontFamily: BODY_FONT,
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 14px',
                background: '#fff',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 24,
                transition: 'background 160ms ease, border-color 160ms ease',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M11 7H3M7 3l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to dashboard
            </button>
          )}

          {error && (
            <div style={{
              background: C.redSoft,
              border: `1px solid ${C.redBorder}`,
              borderRadius: 10,
              padding: '14px 18px',
              color: C.redText,
              fontSize: 14,
            }}>{error}</div>
          )}

          {!flags && !error && <AlertsSkeleton />}

          {flags && (
            <>
              {/* Page heading */}
              <h1 style={{
                margin: '0 0 8px',
                fontFamily: HEADING_FONT,
                fontSize: 30,
                fontWeight: 700,
                color: C.text,
                letterSpacing: '-0.025em',
                lineHeight: 1.15,
              }}>
                Safeguarding alerts
              </h1>
              <p style={{
                margin: '0 0 24px',
                fontSize: 13,
                color: C.textMuted,
                lineHeight: 1.55,
                maxWidth: 620,
              }}>
                These are automatically recorded and retained for 7 years under UK safeguarding requirements. They cannot be deleted.
              </p>

              {flags.length === 0 ? (
                /* All-clear state */
                <div style={{
                  background: C.greenSoft,
                  border: `1px solid ${C.greenBorder}`,
                  borderRadius: 14,
                  padding: '40px 24px',
                  textAlign: 'center',
                }}>
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: '#fff',
                    border: `2px solid ${C.greenBorder}`,
                    margin: '0 auto 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(16, 185, 129, 0.18)',
                  }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12.5l4.5 4.5L19 7.5" stroke={C.greenDark} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h2 style={{
                    margin: '0 0 6px',
                    fontFamily: HEADING_FONT,
                    fontSize: 20,
                    fontWeight: 700,
                    color: C.greenText,
                    letterSpacing: '-0.015em',
                  }}>All clear</h2>
                  <p style={{
                    margin: 0,
                    fontSize: 14,
                    color: C.greenText,
                    lineHeight: 1.5,
                    maxWidth: 360,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}>
                    No safeguarding alerts on record. Lantern will let you know right away if anything comes up.
                  </p>
                </div>
              ) : (
                <>
                  {/* Awaiting review banner */}
                  {unreviewed.length > 0 && (
                    <div style={{
                      marginBottom: 20,
                      background: '#fff',
                      border: `1px solid ${C.amberBorder}`,
                      borderLeft: `4px solid ${C.amber}`,
                      borderRadius: 10,
                      padding: '14px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                    }}>
                      <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: C.amberSoft,
                        color: C.amber,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M8 4.5v4M8 10.8v.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: HEADING_FONT,
                          fontWeight: 700,
                          fontSize: 15,
                          color: C.amberText,
                          letterSpacing: '-0.005em',
                        }}>
                          {unreviewed.length} alert{unreviewed.length !== 1 ? 's' : ''} awaiting review
                        </div>
                        <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>
                          Take a moment to read through and decide if any action is needed.
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {flags.map(f => (
                      <FlagCard
                        key={f.id}
                        flag={f}
                        onViewSession={() => navigate(`/parent/session/${f.session_id}`)}
                        onReviewed={handleReviewed}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
